import React from "react";
import { useAuth } from "../auth/useAuth";
import type { TemplateVariablePreviewValueSet } from "./templateVariablePreviewValues";

type StudioTemplatePreviewDto = {
  id: string;
  name?: string | null;
  shortName?: string | null;
  code?: string | null;
  street?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  bankAccountHolder?: string | null;
  iban?: string | null;
  bic?: string | null;
  costCenter?: string | null;
};

function clean(value: unknown): string | undefined {
  const text = String(value ?? "").trim();
  return text.length > 0 ? text : undefined;
}

function buildStudioPreviewValues(studio: StudioTemplatePreviewDto | null | undefined): TemplateVariablePreviewValueSet {
  if (!studio) return {};
  const result: TemplateVariablePreviewValueSet = {};
  const entries: Array<[string, unknown]> = [
    ["studioName", studio.name],
    ["studioShortName", studio.shortName],
    ["studioCode", studio.code],
    ["studioStreet", studio.street],
    ["studioZip", studio.zip],
    ["studioCity", studio.city],
    ["studioCountry", studio.country],
    ["studioPhone", studio.phone],
    ["studioEmail", studio.email],
    ["studioWebsite", studio.website],
    ["studioCostCenter", studio.costCenter],
    ["studioBankAccountHolder", studio.bankAccountHolder],
    ["studioIban", studio.iban],
    ["studioBic", studio.bic]
  ];
  entries.forEach(([key, value]) => {
    const next = clean(value);
    if (next) result[key] = next;
  });
  return result;
}

export function useStudioTemplatePreviewValues(selectedStudioIds?: string[] | null): TemplateVariablePreviewValueSet {
  const auth = useAuth();
  const [studios, setStudios] = React.useState<StudioTemplatePreviewDto[]>([]);

  React.useEffect(() => {
    let mounted = true;
    auth.api
      .get<StudioTemplatePreviewDto[]>("/api/v1/studios")
      .then((list) => {
        if (mounted) setStudios(list ?? []);
      })
      .catch(() => {
        if (mounted) setStudios([]);
      });
    return () => {
      mounted = false;
    };
  }, [auth.api]);

  return React.useMemo(() => {
    const selectedIds = (selectedStudioIds ?? []).map(String).filter(Boolean);
    const selectedStudio = selectedIds.length > 0
      ? selectedIds.map((id) => studios.find((studio) => String(studio.id) === id)).find(Boolean)
      : null;
    return buildStudioPreviewValues(selectedStudio ?? studios[0]);
  }, [selectedStudioIds, studios]);
}
